import { clamp, interpolateMat3, invertMat3, multiplyMat3, transformMat3 } from "@/core/utils/math"
import { XYtoXYZ, XYZtoXY } from "@/core/utils/color-conversion"
import { getAdaptionMatrix, illuminants } from "@/core/utils/color"
import { GenericWhiteBalance } from "./generic-white-balance"
import { RawInfo } from "@/lib/raw/fastlib"
import { mat3, vec2, vec3 } from "gl-matrix"
import { XYtoWhiteBalance } from "@/core/utils/temperature"
import { XYCoord } from "./xy-coord"

export class RAWColorWhiteBalance extends GenericWhiteBalance {
	private forwardMatrix: mat3 | null = null

	private wbCoeffs: vec3 = [1, 1, 1]
	private whiteXY: vec2 = [1, 1]
	private camToXYZ: mat3 = mat3.create()
	private temperature = 0
	private tint = 0

	constructor(info: RawInfo) {
		super(info)
		this.compute()
	}

	public get neutralToXY(): vec2 {
		return this.whiteXY
	}

	public get camToPCS(): mat3 {
		return this.camToXYZ
	}

	public get PCSToCam(): mat3 {
		return invertMat3(this.camToXYZ)
	}

	public get whiteBalanceMultipliers(): vec3 {
		return this.wbCoeffs
	}

	public get whiteBalance(): { tint: number; temperature: number } {
		return {
			tint: this.tint,
			temperature: this.temperature,
		}
	}

	private compute() {
		this.wbCoeffs = this.getWBCoeffs(this.info)
		this.whiteXY = this.getNeutralToXY(this.info)

		const [temp, tint] = XYtoWhiteBalance(this.whiteXY)

		this.temperature = temp
		this.tint = tint

		this.camToXYZ = this.getCamToXYZ(this.info, this.whiteXY)
	}

	private getWhiteBalance(info: RawInfo, camToXYZ: mat3) {
		const neutral = this.getCameraNeutral(info)
		const XYZ = transformMat3(neutral, camToXYZ)

		XYZ[0] /= XYZ[1]
		XYZ[2] /= XYZ[1]
		XYZ[1] /= XYZ[1]

		return XYZ
	}

	private getNeutralToXY(info: RawInfo) {
		const neutral = this.getCameraNeutral(info)

		const maxPasses = 30

		let last: vec2 = [XYCoord.D50().x, XYCoord.D50().y]

		for (let pass = 0; pass < maxPasses; pass++) {
			const xyzToCamera = this.findXYZtoCamera(info, last)
			const xyzToCameraInv = invertMat3(xyzToCamera)

			const next = XYZtoXY(transformMat3(neutral, xyzToCameraInv))

			if (
				// XY coords have converged
				next[0] - last[0] + (next[1] + last[1]) <
				0.0000001
			) {
				return next
			}

			// XY coords have not converged, so average the last two estimates
			if (pass === maxPasses - 1) {
				next[0] = (last[0] + next[0]) / 2
				next[1] = (last[1] + next[1]) / 2
			}

			last = next
		}

		return last
	}

	private getCamToXYZ(info: RawInfo, whiteXY: vec2) {
		// CAM to XYZ
		let camToXYZ: mat3

		// prettier-ignore
		// TODO: get calibration matrix from DCP profile
		const calibrationMatrix: mat3 = [
			1, 0, 0,
			0, 1, 0,
			0, 0, 1
		]

		if (this.forwardMatrix) {
			const colorMatrix = this.findXYZtoCamera(info, whiteXY)
			const cameraWhite = transformMat3(XYtoXYZ(whiteXY), colorMatrix)

			const whiteScale = 1 / Math.max(...cameraWhite)

			cameraWhite[0] = clamp(cameraWhite[0] * whiteScale, 0.001, 1.0)
			cameraWhite[1] = clamp(cameraWhite[1] * whiteScale, 0.001, 1.0)
			cameraWhite[2] = clamp(cameraWhite[2] * whiteScale, 0.001, 1.0)

			const toReference = invertMat3(calibrationMatrix)
			const refCameraWhite = transformMat3(cameraWhite, toReference)

			// prettier-ignore
			const whiteMatrix: mat3 = [
				refCameraWhite[0], 0, 0,
				0, refCameraWhite[1], 0,
				0, 0, refCameraWhite[2]
			]

			camToXYZ = multiplyMat3(this.forwardMatrix, invertMat3(whiteMatrix))
			camToXYZ = multiplyMat3(camToXYZ, toReference)
		} else {
			let mat = this.getColorMatrix(info)
			mat = this.normalizeColorMatrix(mat)
			camToXYZ = invertMat3(mat)

			const whiteBalance = this.getWhiteBalance(info, camToXYZ)
			const adaptionMatrix = getAdaptionMatrix(whiteBalance, illuminants.D50)

			camToXYZ = multiplyMat3(adaptionMatrix, camToXYZ)
		}

		if (this.shouldApplyAnalogBalance(info) && info.analog_balance.length === 3) {
			const [x, y, z] = info.analog_balance as unknown as vec3
			// prettier-ignore
			const balanceMat: mat3 = [
				1/x, 0, 0,
				0, 1/y, 0,
				0, 0, 1/z
			]

			camToXYZ = multiplyMat3(camToXYZ, balanceMat)
		}

		return camToXYZ
	}

	private shouldApplyAnalogBalance(info: RawInfo) {
		// Three-channel non-CFA DNGs are already decoded into LinearRaw RGB samples.
		// Applying AnalogBalance again suppresses the red/blue channels and creates a green cast.
		return info.is_cfa || info.components_per_pixel !== 3
	}

	private findXYZtoCamera(info: RawInfo, whiteXY: vec2) {
		const [temperature] = XYtoWhiteBalance(whiteXY)
		const defaultColorMatrix = this.getColorMatrix(info)

		const temperature1 = info.profile?.calibrationTemperature1 ?? 6500
		const temperature2 = info.profile?.calibrationTemperature2 ?? 6500

		const colorMatrix1 = info.profile?.colorMatrix1 ?? defaultColorMatrix
		const colorMatrix2 = info.profile?.colorMatrix2 ?? defaultColorMatrix

		const forwardMatrix1 = info.profile?.forwardMatrix1
		const forwardMatrix2 = info.profile?.forwardMatrix2

		let g: number

		if (temperature <= temperature1) {
			g = 1
		} else if (temperature >= temperature2) {
			g = 0
		} else {
			const invT = 1 / temperature
			g = (invT - 1 / temperature2) / (1 / temperature1 - 1 / temperature2)
		}

		let colorMatrix: mat3

		if (g >= 1) {
			colorMatrix = colorMatrix1
		} else if (g <= 0) {
			colorMatrix = colorMatrix2
		} else {
			colorMatrix = interpolateMat3(colorMatrix1, colorMatrix2, g)
		}

		// Interpolate forward matrix
		if (forwardMatrix1 && forwardMatrix2) {
			if (g >= 1) {
				this.forwardMatrix = forwardMatrix1
			} else if (g <= 0) {
				this.forwardMatrix = forwardMatrix2
			} else {
				this.forwardMatrix = interpolateMat3(forwardMatrix1, forwardMatrix2, g)
			}
		} else {
			this.forwardMatrix = null
		}

		// TODO: interpolate reduction matrix and calibration matrix

		return this.normalizeColorMatrix(colorMatrix)
	}

	private getWBCoeffs(info: RawInfo): vec3 {
		const x = info.wb_coeffs[0] || 1
		const y = info.wb_coeffs[1] || 1
		const z = info.wb_coeffs[2] || 1

		return [x / y, 1, z / y]
	}

	private getCameraNeutral(info: RawInfo): vec3 {
		const [x, y, z] = this.getWBCoeffs(info)

		if (
			info.analog_balance.length === 3 &&
			Math.abs(x - 1) < 0.001 &&
			Math.abs(z - 1) < 0.001
		) {
			return this.getCameraNeutralFromAnalogBalance(info)
		}

		return [1 / x, 1 / y, 1 / z]
	}

	private getCameraNeutralFromAnalogBalance(info: RawInfo): vec3 {
		const x = info.analog_balance[0] || 1
		const y = info.analog_balance[1] || 1
		const z = info.analog_balance[2] || 1

		return [y / x, 1, y / z]
	}

	private getColorMatrix(info: RawInfo): mat3 {
		const adobeCoeffFactor = 10_000

		let mat: mat3 = info.color_matrix.slice(0) as unknown as mat3

		if (mat[0] === 0) {
			// Camera color mat is not defined
			// prettier-ignore
			return mat3.create()
		}

		if (mat[0] > 10) {
			mat = mat.map((n) => n / adobeCoeffFactor) as mat3
		}

		return this.normalizeColorMatrix(mat)
	}

	private normalizeColorMatrix(mat: mat3) {
		const coord = transformMat3(illuminants.D50, mat)

		const max = Math.max(...coord)

		if (max > 0.0 && (max < 0.99 || max > 1.01)) {
			const scale = 1 / max
			// prettier-ignore
			mat = multiplyMat3(mat, [
				scale, 0, 0,
				0, scale, 0,
				0, 0, scale
			])
		}

		return mat
	}
}
